import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import api from "api/client";

const recipientShape = PropTypes.shape({
  userId: PropTypes.number.isRequired,
  username: PropTypes.string,
  name: PropTypes.string,
  hasPlayer: PropTypes.bool,
  online: PropTypes.bool,
});

function RecipientChips({ value, onRemove }) {
  return (
    <MDBox display="flex" gap={0.5} flexWrap="wrap">
      {value.map((r) => (
        <Chip
          key={r.userId}
          size="small"
          color={r.online ? "success" : "default"}
          label={r.name || r.username}
          onDelete={() => onRemove(r.userId)}
        />
      ))}
    </MDBox>
  );
}

RecipientChips.propTypes = {
  value: PropTypes.arrayOf(recipientShape).isRequired,
  onRemove: PropTypes.func.isRequired,
};

/** Tìm trong bảng user theo tài khoản, user id hoặc tên nhân vật; tick để chọn, bấm Xong mới áp dụng. */
function RecipientDialog({ value, onApply, onClose }) {
  const [draft, setDraft] = useState(value);
  const [input, setInput] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      api
        .get("/api/admin/mail/recipients", { q: input.trim(), size: 50 })
        .then((res) => active && setResults(res))
        .catch(() => active && setResults([]))
        .finally(() => active && setLoading(false));
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [input]);

  const picked = (userId) => draft.some((d) => d.userId === userId);
  const toggle = (o) =>
    setDraft(picked(o.userId) ? draft.filter((d) => d.userId !== o.userId) : [...draft, o]);
  const selectable = results.filter((o) => o.hasPlayer && !picked(o.userId));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Chọn người nhận</DialogTitle>
      <DialogContent>
        <MDBox pt={1} mb={2}>
          <MDInput
            fullWidth
            autoFocus
            label="Tìm theo tài khoản, tên nhân vật hoặc user id"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon>search</Icon>
                </InputAdornment>
              ),
              endAdornment: loading ? <CircularProgress color="inherit" size={16} /> : null,
            }}
          />
        </MDBox>
        <MDBox display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
          <MDBox flexGrow={1} minWidth={0}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <MDTypography variant="button" fontWeight="medium">
                Kết quả ({results.length})
              </MDTypography>
              <MDButton
                size="small"
                variant="text"
                color="info"
                disabled={selectable.length === 0}
                onClick={() => setDraft([...draft, ...selectable])}
              >
                <Icon>done_all</Icon>&nbsp;Chọn tất cả ({selectable.length})
              </MDButton>
            </MDBox>
            <MDBox
              maxHeight={420}
              overflow="auto"
              border="1px solid"
              borderColor="divider"
              borderRadius="md"
            >
              {results.map((o) => (
                <MDBox
                  key={o.userId}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  px={1}
                  py={0.5}
                  onClick={() => o.hasPlayer && toggle(o)}
                  sx={{
                    cursor: o.hasPlayer ? "pointer" : "default",
                    opacity: o.hasPlayer ? 1 : 0.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: picked(o.userId) ? "action.selected" : "transparent",
                    "&:hover": o.hasPlayer ? { bgcolor: "action.hover" } : undefined,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={picked(o.userId)}
                    disabled={!o.hasPlayer}
                    readOnly
                    tabIndex={-1}
                    sx={{ p: 0.5 }}
                  />
                  <MDBox flexGrow={1} minWidth={0} lineHeight={1.2}>
                    <MDTypography variant="button" fontWeight="medium" display="block" noWrap>
                      {o.name || "(chưa tạo nhân vật)"}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      #{o.userId} · {o.username}
                    </MDTypography>
                  </MDBox>
                  {o.online && <Chip size="small" color="success" label="Online" />}
                </MDBox>
              ))}
              {!loading && results.length === 0 && (
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Không tìm thấy tài khoản
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
          <MDBox width={{ md: 260 }} flexShrink={0}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <MDTypography variant="button" fontWeight="medium">
                Đã chọn ({draft.length})
              </MDTypography>
              {draft.length > 0 && (
                <MDButton
                  size="small"
                  variant="text"
                  color="secondary"
                  onClick={() => setDraft([])}
                >
                  Bỏ hết
                </MDButton>
              )}
            </MDBox>
            <MDBox maxHeight={420} overflow="auto">
              {draft.length === 0 ? (
                <MDTypography variant="caption" color="text">
                  Chưa chọn ai. Bấm vào dòng bên trái để chọn.
                </MDTypography>
              ) : (
                <RecipientChips
                  value={draft}
                  onRemove={(userId) => setDraft(draft.filter((d) => d.userId !== userId))}
                />
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Huỷ
        </MDButton>
        <MDButton variant="gradient" color="info" onClick={() => onApply(draft)}>
          Xong ({draft.length} người)
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

RecipientDialog.propTypes = {
  value: PropTypes.arrayOf(recipientShape).isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/** Chọn nhiều người nhận qua hộp thoại, ngoài form chỉ hiện danh sách đã chọn. */
function RecipientPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);

  return (
    <MDBox border="1px solid" borderColor="divider" borderRadius="md" p={1.5}>
      <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={value.length ? 1 : 0}>
        <MDTypography variant="button" fontWeight="medium" sx={{ flexGrow: 1 }}>
          Người nhận: {value.length === 0 ? "chưa chọn" : `${value.length} người`}
        </MDTypography>
        {value.length > 0 && (
          <MDButton size="small" variant="text" color="secondary" onClick={() => onChange([])}>
            <Icon>clear_all</Icon>&nbsp;Bỏ chọn hết
          </MDButton>
        )}
        <MDButton size="small" variant="outlined" color="info" onClick={() => setOpen(true)}>
          <Icon>person_search</Icon>&nbsp;{value.length ? "Chọn thêm / sửa" : "Chọn người nhận"}
        </MDButton>
      </MDBox>
      {value.length > 0 && (
        <RecipientChips
          value={value}
          onRemove={(userId) => onChange(value.filter((r) => r.userId !== userId))}
        />
      )}
      {open && (
        <RecipientDialog
          value={value}
          onClose={() => setOpen(false)}
          onApply={(next) => {
            onChange(next);
            setOpen(false);
          }}
        />
      )}
    </MDBox>
  );
}

RecipientPicker.propTypes = {
  value: PropTypes.arrayOf(recipientShape).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default RecipientPicker;
